import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import Header from './header';
import Footer from './footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faReply, faTimes, faUserMd, faUsers, faComments, faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { timeAgo } from '../utils/functions';
import './GroupCounselling.css';

const GroupCounselling = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    
    const [group, setGroup] = useState(null);
    const [counsellors, setCounsellors] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [currentView, setCurrentView] = useState('main'); // main, chat, sessions
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [quotedMessage, setQuotedMessage] = useState(null);
    const [isCounsellor, setIsCounsellor] = useState(false);
    const [counsellorInfo, setCounsellorInfo] = useState(null);
    const [viewerRole, setViewerRole] = useState(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    // Fetch group information
    const fetchGroup = async () => {
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            
            const requestData = { 
                user_id: user.user_id,
                status: 'active'
            };
            console.log('Fetching group with data:', requestData);
            console.log('User object:', user);
            console.log('User ID from user object:', user.user_id);
            console.log('User ID type:', typeof user.user_id);
            console.log('Group ID:', groupId);
            //console.log('Backend URL:', backendUrl);
            
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            
            console.log('Response status:', response.status);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Groups API response:', data);
            
            if (data.success) {
                // Check if data.data exists and has groups
                if (data.data && Array.isArray(data.data)) {
                    console.log('All available groups:', data.data);
                    console.log('Looking for group ID:', groupId);
                    
                    const groupData = data.data.find(g => g.group_id == groupId);
                    console.log('Found group data:', groupData);
                    
                    if (groupData) {
                        setGroup(groupData);
                    } else {
                        console.log('Available group IDs:', data.data.map(g => g.group_id));
                        setError(`Group not found or you are not a member. Available groups: ${data.data.map(g => g.group_id).join(', ')}`);
                    }
                } else if (data.data && data.data.groups && Array.isArray(data.data.groups)) {
                    console.log('All available groups:', data.data.groups);
                    console.log('Looking for group ID:', groupId);
                    
                    const groupData = data.data.groups.find(g => g.group_id == groupId);
                    console.log('Found group data:', groupData);
                    
                    if (groupData) {
                        setGroup(groupData);
                    } else {
                        console.log('Available group IDs:', data.data.groups.map(g => g.group_id));
                        setError(`Group not found or you are not a member. Available groups: ${data.data.groups.map(g => g.group_id).join(', ')}`);
                    }
                } else {
                    console.error('Unexpected data structure:', data);
                    setError('Unexpected response format from server');
                }
            } else {
                setError(data.message || data.messages || 'Failed to fetch group information');
            }
        } catch (err) {
            console.error('Error fetching group:', err);
            setError(`Failed to fetch group information: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Check if user is a counsellor for this group
    const checkCounsellorStatus = async () => {
        try {
            const response = await fetch(`${backendUrl}/check_user_counsellor_status.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setIsCounsellor(data.is_counsellor);
                setCounsellorInfo(data.counsellor_info);
            } else {
                console.error('Failed to check counsellor status:', data.message);
            }
        } catch (err) {
            console.error('Error checking counsellor status:', err);
        }
    };

    // Fetch counsellors for this group
    const fetchGroupCounsellors = async () => {
        try {
            setLoading(true);
            
            const requestData = { 
                group_id: groupId, 
                user_id: user.user_id 
            };
            
            console.log('Fetching counsellors with data:', requestData);
            console.log('User object:', user);
            console.log('Group ID:', groupId);
            
            const response = await fetch(`${backendUrl}/get_group_counsellors.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            const data = await response.json();
            
            console.log('Counsellors API response:', data);
            
            if (data.success) {
                setCounsellors(data.data.counsellors || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('Error fetching counsellors:', err);
            setError('Failed to fetch counsellors');
        } finally {
            setLoading(false);
        }
    };

    // Fetch user's counselling sessions for this group
    const fetchSessions = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_my_counselling_sessions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    user_id: user.user_id,
                    group_id: groupId
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setConversations(data.data.sessions || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch sessions');
        } finally {
            setLoading(false);
        }
    };

    // Start a counselling conversation
    const startConversation = async (counsellor_id) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/start_counselling_conversation.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId, 
                    member_id: user.user_id,
                    counsellor_id: counsellor_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                // Load the conversation
                await loadConversation(data.data.conversation_id);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to start conversation');
        } finally {
            setLoading(false);
        }
    };

    // Load a conversation
    const loadConversation = async (conversation_id) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_counselling_messages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    conversation_id: conversation_id, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSelectedConversation(data.data.conversation);
                setViewerRole(data.data.conversation?.viewer_role || null);
                setMessages(data.data.messages || []);
                setCurrentView('chat');
                
                // Start polling for new messages
                startMessagePolling(conversation_id);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    };

    // Start polling for new messages
    const startMessagePolling = (conversation_id) => {
        // Clear any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        
        // Poll every 3 seconds for new messages
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const response = await fetch(`${backendUrl}/get_counselling_messages.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        conversation_id: conversation_id, 
                        user_id: user.user_id 
                    })
                });
                const data = await response.json();
                
                if (data.success && data.data.messages) {
                    setMessages(data.data.messages || []);
                    if (data.data.conversation?.viewer_role) {
                        setViewerRole(data.data.conversation.viewer_role);
                    }
                }
            } catch (err) {
                console.error('Error polling for messages:', err);
            }
        }, 3000);
    };

    // Stop polling for messages
    const stopMessagePolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    // Send a message
    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/send_counselling_message.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversation_id: Number(selectedConversation.id),
                    sender_id: Number(user.user_id),
                    message_text: newMessage.trim(),
                    message_type: 'text',
                    cloudinary_url: '',
                    file_name: '',
                    file_size: 0,
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setNewMessage('');
                setQuotedMessage(null);
                // Reload messages immediately
                await loadConversation(selectedConversation.id);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    // Quote a message
    const quoteMessage = (message) => {
        setQuotedMessage(message);
        textareaRef.current?.focus();
    };

    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Format time for display using the existing timeAgo function
    const formatTime = (timestamp) => {
        return timeAgo(timestamp);
    };

    const counsellorsForMemberChat = user?.user_id
        ? counsellors.filter(
            (row) => row?.counsellor?.id != null && String(row.counsellor.id) !== String(user.user_id)
        )
        : [];
    const isCounsellorChatView = viewerRole === 'counsellor';

    useEffect(() => {
        if (user && user.user_id && groupId) {
            console.log('User and groupId available, fetching data...');
            fetchGroup();
            checkCounsellorStatus();
            fetchGroupCounsellors();
            fetchSessions();
        } else {
            console.log('User or groupId not available:', { user, groupId });
        }
    }, [user, groupId]);

    // Auto-scroll when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Cleanup polling on component unmount
    useEffect(() => {
        return () => {
            stopMessagePolling();
        };
    }, []);

    // Stop polling when leaving chat view
    useEffect(() => {
        if (currentView !== 'chat') {
            stopMessagePolling();
        }
    }, [currentView]);

    const renderMainView = () => (
        <div className="container-fluid p-3">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex align-items-center mb-3">
                        <div>
                            <h2 className="mb-0">📞 Counselling - {group?.name}</h2>
                            <p className="text-muted mb-0">
                                {isCounsellor ? 'Manage your counselling sessions' : 'Get free counselling support from group counsellors'}
                            </p>
                            {!isCounsellor && (
                                <p className="text-muted small mb-0 mt-2">
                                    Chat anonymously: your counsellor sees your messages, not your name.
                                </p>
                            )}
                        </div>
                    </div>

                    {isCounsellor ? (
                        // Counsellor View - Show their sessions
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header d-flex align-items-center">
                                        <FontAwesomeIcon icon={faComments} className="me-2" />
                                        <h5 className="mb-0">My Counselling Sessions</h5>
                                        <span className="badge bg-primary ms-auto">{conversations.length} sessions</span>
                                    </div>
                                    <div className="card-body">
                                        {conversations.length > 0 ? (
                                            <div className="row">
                                                {conversations.map(session => (
                                                    <div key={session.session_id} className="col-md-6 col-lg-4 mb-3">
                                                        <div 
                                                            className="card h-100 counselling-session-card" 
                                                            onClick={() => loadConversation(session.session_id)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="card-body">
                                                                <div className="d-flex align-items-center mb-3">
                                                                    <div className="me-3">
                                                                        {session.other_participant.photo ? (
                                                                            <img 
                                                                                src={session.other_participant.photo} 
                                                                                alt={session.other_participant.name}
                                                                                className="rounded-circle"
                                                                                style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                                            />
                                                                        ) : (
                                                                            <div 
                                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                                style={{ width: '50px', height: '50px' }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faUserMd} />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-1">{session.other_participant.name}</h6>
                                                                        <small className="text-muted">Anonymous member</small>
                                                                    </div>
                                                                    {session.unread_count > 0 && (
                                                                        <span className="badge bg-danger">{session.unread_count}</span>
                                                                    )}
                                                                </div>
                                                                
                                                                {session.last_message && (
                                                                    <p className="card-text text-muted small mb-2">
                                                                        <em>&quot;{session.last_message.text}&quot;</em>
                                                                    </p>
                                                                )}
                                                                
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <small className="text-muted">
                                                                        {timeAgo(session.last_message_at || session.created_at)}
                                                                    </small>
                                                                    <FontAwesomeIcon icon={faComments} className="text-primary" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <FontAwesomeIcon icon={faComments} size="3x" className="text-muted mb-3" />
                                                <h5 className="text-muted">No Active Sessions</h5>
                                                <p className="text-muted">You don&apos;t have any counselling sessions yet.</p>
                                                <p className="text-muted">Members will be able to start conversations with you once they see you as an available counsellor.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Member View - Show available counsellors
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header d-flex align-items-center">
                                        <FontAwesomeIcon icon={faUsers} className="me-2" />
                                        <h5 className="mb-0">Available Counsellors</h5>
                                        <span className="badge bg-success ms-auto">{counsellorsForMemberChat.length} counsellors</span>
                                    </div>
                                    <div className="card-body">
                                        {counsellorsForMemberChat.length > 0 ? (
                                            <div className="row">
                                                {counsellorsForMemberChat.map(counsellor => {
                                                    const existingSession = conversations.find(session => 
                                                        session.other_participant.id === counsellor.counsellor.id
                                                    );
                                                    
                                                    return (
                                                        <div key={counsellor.counsellor.id} className="col-md-6 col-lg-4 mb-3">
                                                            <div 
                                                                className="card h-100 counselling-counsellor-card" 
                                                                onClick={() => startConversation(counsellor.counsellor.id)} 
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <div className="card-body text-center">
                                                                    <div className="mb-3">
                                                                        {counsellor.counsellor.photo ? (
                                                                            <img 
                                                                                src={counsellor.counsellor.photo} 
                                                                                alt={counsellor.counsellor.name}
                                                                                className="rounded-circle"
                                                                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                                            />
                                                                        ) : (
                                                                            <div 
                                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto"
                                                                                style={{ width: '80px', height: '80px' }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faUserMd} size="2x" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <h6 className="card-title">{counsellor.counsellor.name}</h6>
                                                                    <p className="card-text">
                                                                        <span className={`badge ${counsellor.counsellor.is_available ? 'bg-success' : 'bg-danger'}`}>
                                                                            {counsellor.counsellor.is_available ? 'Available' : 'Busy'}
                                                                        </span>
                                                                    </p>
                                                                    <small className="text-muted d-block mb-2">
                                                                        {Number(counsellor.counsellor.active_conversations ?? 0)} active conversations
                                                                    </small>
                                                                    {existingSession && (
                                                                        <div className="alert alert-info py-2 mb-0">
                                                                            <small>
                                                                                <FontAwesomeIcon icon={faComments} className="me-1" />
                                                                                {existingSession.unread_count > 0 ? 
                                                                                    `${existingSession.unread_count} unread messages` : 
                                                                                    'Continue conversation'
                                                                                }
                                                                            </small>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
                                                <h5 className="text-muted">No counsellors you can message</h5>
                                                <p className="mb-2">
                                                    {counsellors.length === 0
                                                        ? 'No counsellors have been assigned to this group yet.'
                                                        : 'You are the only counsellor here, or every counsellor is you — ask an admin to appoint another counsellor if you need support.'}
                                                </p>
                                                <p className="mb-3">Contact a group admin to assign counsellors from group members.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* My Sessions Section - Only show for members, not counsellors */}
                    {!isCounsellor && conversations.length > 0 && (
                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header d-flex align-items-center">
                                        <FontAwesomeIcon icon={faComments} className="me-2" />
                                        <h5 className="mb-0">My Sessions</h5>
                                        <button 
                                            className="btn btn-sm btn-outline-primary ms-auto"
                                            onClick={() => setCurrentView('sessions')}
                                        >
                                            View All
                                        </button>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            {conversations.slice(0, 3).map(session => (
                                                <div key={session.session_id} className="col-md-4 mb-3">
                                                    <div 
                                                        className="card h-100 counselling-session-card" 
                                                        onClick={() => loadConversation(session.session_id)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div className="card-body">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <div className="me-2">
                                                                    {session.other_participant.photo ? (
                                                                        <img 
                                                                            src={session.other_participant.photo} 
                                                                            alt={session.other_participant.name}
                                                                            className="rounded-circle"
                                                                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                        />
                                                                    ) : (
                                                                        <div 
                                                                            className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                                                                            style={{ width: '40px', height: '40px' }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faUserMd} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <h6 className="mb-0 small">{session.other_participant.name}</h6>
                                                                    <small className="text-muted">
                                                                        {session.user_role === 'member' ? 'Member' : 'Counsellor'}
                                                                    </small>
                                                                </div>
                                                                {session.unread_count > 0 && (
                                                                    <span className="badge bg-danger">{session.unread_count}</span>
                                                                )}
                                                            </div>
                                                            {session.last_message && (
                                                                <p className="card-text text-muted small mb-1">
                                                                    <em>"{session.last_message.text.substring(0, 50)}..."</em>
                                                                </p>
                                                            )}
                                                            <small className="text-muted">
                                                                {timeAgo(session.last_message_at || session.created_at)}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderChatView = () => (
        <div className="chat-container">
            {/* Chat Header */}
            <div className="chat-header">
                <div className="d-flex align-items-center">
                    <button 
                        className="btn btn-link text-dark me-3"
                        onClick={() => {
                            setViewerRole(null);
                            setCurrentView('main');
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="d-flex align-items-center">
                        <div className="chat-avatar me-3">
                            {!isCounsellorChatView && selectedConversation?.counsellor.photo ? (
                                <img 
                                    src={selectedConversation.counsellor.photo} 
                                    alt={selectedConversation.counsellor.name}
                                    className="rounded-circle"
                                />
                            ) : (
                                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center">
                                    <FontAwesomeIcon icon={isCounsellorChatView ? faUserSecret : faUserMd} />
                                </div>
                            )}
                        </div>
                        <div>
                            <h6 className="mb-0">
                                {isCounsellorChatView ? 'Anonymous member' : (selectedConversation?.counsellor?.name || 'Counsellor')}
                            </h6>
                            <small className="text-muted">
                                {isCounsellorChatView
                                    ? 'You do not see this person\'s name. Replies remain confidential.'
                                    : 'Your name is hidden from the counsellor.'}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat Messages */}
            <div className="chat-messages">
                {messages.length === 0 ? (
                    <div className="empty-chat">
                        <div className="text-center">
                            <FontAwesomeIcon icon={faUserMd} size="3x" className="text-muted mb-3" />
                            <h5 className="text-muted">Start your counselling conversation</h5>
                            <p className="text-muted">
                                {isCounsellorChatView
                                    ? 'Send a message to reply to this member anonymously.'
                                    : `Send a message to begin your session with ${selectedConversation?.counsellor?.name || 'your counsellor'}.`}
                            </p>
                        </div>
                    </div>
                ) : (
                    messages.map((message, index) => {
                        const isOwn = message.sender && message.sender.id != null && String(message.sender.id) === String(user.user_id);
                        const showTime = true; // Always show timestamp
                        
                        return (
                            <div key={message.id} className={`message-wrapper ${isOwn ? 'own' : 'other'}`}>
                                <div className="message-bubble">
                                    {/* Quoted message */}
                                    {message.quoted_message && (
                                        <div className="quoted-message">
                                            <div className="quoted-content">
                                                <strong>{message.quoted_message.sender.name}:</strong>
                                                <p>{message.quoted_message.message_text}</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Message content */}
                                    <div className="message-content">
                                        <p className="mb-1">{message.message_text}</p>
                                    </div>
                                    
                                    {/* Message time */}
                                    {showTime && (
                                        <div className="message-time">
                                            {formatTime(message.created_at)}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Reply button */}
                                {!isOwn && (
                                    <button 
                                        className="reply-btn"
                                        onClick={() => quoteMessage(message)}
                                        title="Reply to this message"
                                    >
                                        <FontAwesomeIcon icon={faReply} />
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Quote Preview */}
            {quotedMessage && (
                <div className="quote-preview">
                    <div className="quote-content">
                        <div className="quote-header">
                            <strong>Replying to {quotedMessage.sender.name}</strong>
                            <button 
                                className="btn btn-sm btn-link text-danger"
                                onClick={() => setQuotedMessage(null)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <p className="quote-text">{quotedMessage.message_text}</p>
                    </div>
                </div>
            )}

            {/* Chat Input */}
            <div className="chat-input">
                <div className="input-group">
                    <textarea
                        ref={textareaRef}
                        className="form-control"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        disabled={loading}
                        rows="1"
                        style={{ resize: 'none', minHeight: '40px', maxHeight: '120px' }}
                    />
                    <button 
                        className="btn btn-primary send-btn"
                        onClick={sendMessage} 
                        disabled={loading || !newMessage.trim()}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                </div>
            </div>
        </div>
    );

    if (!user || !user.user_id) {
        return <div className="container-fluid p-3">
            <div className="alert alert-warning" role="alert">
                <h5>Authentication Required</h5>
                <p>Please log in to access counselling services.</p>
            </div>
        </div>;
    }

    if (loading && !group) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading counselling...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column">
            {currentView === 'main' && (
                <>
                    <Header />
                    <div className="flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                        {renderMainView()}
                    </div>
                    <Footer />
                </>
            )}
            {currentView === 'chat' && renderChatView()}
        </div>
    );
};

export default GroupCounselling;
